import { RecapJourney } from "./RecapJourney";
import { useBookingStore } from "@stores/useBookingStore";
import { useTranslation } from "react-i18next";
import { Icon, Button } from "@thetreep/ui";
import { useLocation, useNavigate } from "react-router";
import { routes } from "@routes";
import { useDAP } from "../dap.hook";
import { addToCart } from "@services/cart";
import { Code, ConnectError } from "@connectrpc/connect";
import * as Sentry from "@sentry/react";
import { useToaster } from "@hooks/useToaster";
import { useNavigationGuard } from "@hooks/useNavigationGuard";
import { useEffect, useRef, useState } from "react";
import { isTrain } from "@services/travel";
import { create } from "@bufbuild/protobuf";
import { LoaderDots } from "@components/common/LoaderDots/LoaderDots";
import { PriceSchema } from "@gen/travel/v1/common_pb";

export function Recap() {
  const {
    outwardOffer,
    inwardOffer,
    setOutwardOffer,
    setInwardOffer,
    outwardResults,
    inwardResults,
  } = useBookingStore();

  const origin = outwardOffer?.solution?.journey?.route?.origin?.name;
  const destination = outwardOffer?.solution?.journey?.route?.destination?.name;
  const { t } = useTranslation();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { dap } = useDAP();
  const { addToast } = useToaster();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { searchRequest } = useBookingStore();

  const processingRef = useRef<HTMLDialogElement>(null);

  useNavigationGuard(isSubmitting);

  useEffect(() => {
    if (!outwardOffer || !inwardOffer) {
      void navigate(routes.SEARCH + search, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (!isSubmitting) return;

    const dialog = processingRef.current;
    dialog?.showModal();

    return () => dialog?.close();
  }, [isSubmitting]);

  const handleModifyJourney = (type: "outward" | "inward") => {
    if (type === "outward") {
      setOutwardOffer(null);
    } else {
      setInwardOffer(null);
    }
    navigate(routes.RESULTS + search, {
      state: {
        searchResponse: type === "outward" ? outwardResults : inwardResults,
        searchRequest: searchRequest,
      },
    });
  };

  function outwardAncillariesPrice() {
    const cost = outwardOffer?.ancillaries?.reduce((acc, a) => {
      const ancillary = outwardOffer?.offer?.ancillaries?.find(
        (a2) => a2.type === a.type,
      );
      if (!ancillary || !ancillary.price) return acc;

      return acc + a.quantity * Number(ancillary.price.amount);
    }, 0);

    return Number(cost) || 0;
  }
  function inwardAncillariesPrice() {
    const cost = inwardOffer?.ancillaries?.reduce((acc, a) => {
      const ancillary = inwardOffer?.offer?.ancillaries?.find(
        (a2) => a2.type === a.type,
      );
      if (!ancillary || !ancillary.price) return acc;

      return acc + a.quantity * Number(ancillary.price.amount);
    }, 0);

    return Number(cost) || 0;
  }

  async function handleAddToCart() {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await addToCart(dap.number);
      await navigate(routes.CART);
    } catch (e: unknown) {
      if (e instanceof ConnectError && e.code === Code.AlreadyExists) {
        addToast("info", e.rawMessage);
        await navigate(routes.CART);
        return;
      }
      console.error(e);
      Sentry.captureException(e, { tags: { flow: "add_to_cart" } });
      if (e instanceof ConnectError && e.rawMessage) {
        addToast("error", e.rawMessage);
      } else {
        addToast("error", t("genericError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  // getPlanePrice returns the price for a flight
  // this is mandatory because we get a outward/inward bundle price
  // this is how its done on the SBT
  function getPlanePrice() {
    return create(PriceSchema, {
      amount: inwardOffer?.offer.initialPrice
        ? inwardOffer?.offer.initialPrice.amount / BigInt(2)
        : BigInt(0),
      currency: inwardOffer?.offer.initialPrice?.currency,
    });
  }

  // getPlanePrice returns the price for a flight
  // this is mandatory because we get a outward/inward bundle price
  // this is how its done on the SBT
  function getOutOfPocketPlanePrice() {
    return create(PriceSchema, {
      amount: inwardOffer?.offer.outOfPocketAmount
        ? inwardOffer?.offer.outOfPocketAmount.amount / BigInt(2)
        : BigInt(0),
      currency: inwardOffer?.offer.outOfPocketAmount?.currency,
    });
  }

  return (
    <div className="flex flex-col gap-10 mb-20 lg:mb-0">
      <div className="flex flex-col gap-1">
        <h1 className="text-[24px] lg:text-[32px] text-strong font-bold font-jakarta">
          {t("booking.roundTrip")}: {origin} → {destination}
        </h1>
      </div>

      <div className="flex flex-col gap-14">
        <div className="flex flex-col gap-4 lg:flex-row">
          {outwardOffer && inwardOffer && (
            <>
              <RecapJourney
                type="outward"
                onModifyJourney={handleModifyJourney}
                offer={outwardOffer?.offer}
                solution={outwardOffer?.solution}
                price={
                  !isTrain(outwardOffer.solution) ? getPlanePrice() : undefined
                }
                outOfPocketAmount={
                  !isTrain(outwardOffer.solution)
                    ? getOutOfPocketPlanePrice()
                    : undefined
                }
                ancillariesPrice={outwardAncillariesPrice()}
              />
              <RecapJourney
                type="inward"
                onModifyJourney={handleModifyJourney}
                offer={inwardOffer?.offer}
                solution={inwardOffer?.solution}
                price={
                  !isTrain(outwardOffer.solution) ? getPlanePrice() : undefined
                }
                outOfPocketAmount={
                  !isTrain(outwardOffer.solution)
                    ? getOutOfPocketPlanePrice()
                    : undefined
                }
                ancillariesPrice={inwardAncillariesPrice()}
              />
            </>
          )}
        </div>
        <div className="flex lg:justify-end [&_button]:w-full lg:[&_button]:w-auto">
          <Button
            variant="primary"
            startEnhancer={<Icon src="basket_fill" color="white" />}
            isLoading={isSubmitting}
            disabled={isSubmitting}
            onClick={handleAddToCart}
          >
            {t("recap.addToCart")}
          </Button>
        </div>
      </div>

      <dialog
        ref={processingRef}
        onCancel={(e) => e.preventDefault()}
        className="w-[min(90vw,440px)] rounded-2xl p-8 pt-16 backdrop:bg-black/50"
      >
        <div className="flex flex-col items-center gap-4 text-center">
          <LoaderDots />
          <h2 className="font-jakarta font-bold text-text-strong text-[20px]">
            {t("recap.processingTitle")}
          </h2>
          <p className="font-roboto text-text-strong text-[14px] lg:text-[16px]">
            {t("recap.processingText")}
          </p>
        </div>
      </dialog>
    </div>
  );
}
